import React from 'react'
import { flushAllDrafts } from '../hooks/draftFlushRegistry'

/**
 * ChunkLoadErrorBoundary — catches React.lazy chunk-load failures
 * (network drop, stale-hash after deploy, 404 on old chunk name) and
 * offers the user a one-click reload.  Prevents the "white screen on
 * Friday at 6pm" scenario flagged in the T2-B pre-code review by all
 * five reviewers.
 *
 * Root cause: React.lazy() rejects the import Promise when the chunk
 * 404s or fails to parse.  React 18 Suspense does NOT catch rejections
 * (only thrown Promises).  Without an error boundary above Suspense
 * the whole route tree crashes into the SentryErrorBoundary's generic
 * reload UI (or, worse in some Electron builds, spins on the fallback
 * forever).
 *
 * Architecture (post-code review refined 2026-08-10 per Sol + Kimi + Grok
 * convergent finding — the naive `componentDidCatch`-rethrow pattern is
 * fragile because React 18 catches the re-throw and can loop):
 *
 * - `getDerivedStateFromError` ALWAYS stores the error (state.error).
 * - `render()` decides propagation: chunk errors show the fallback UI,
 *   non-chunk errors are re-thrown from `render()` itself — React 18
 *   propagates render-throws cleanly to the parent boundary (typically
 *   SentryErrorBoundary), no loop possible.
 * - `componentDidCatch` is used ONLY for the auto-reload side effect
 *   (no rethrow).  Reads sessionStorage['lextrack_chunk_reload_attempted'];
 *   if absent, sets it + `window.location.reload()`.  If already set
 *   (second failure), the manual reload UI is shown instead.
 * - The manual reload button does NOT clear the flag — it stays set as
 *   protection against auto-reload loops when the deploy is genuinely
 *   broken.  The flag only clears via `useResetChunkReloadFlag` which
 *   is called INSIDE the boundary's successful-mount path (see Layout
 *   `InnerRoute` wrapper).  This guarantees the flag only clears after
 *   the app has been demonstrably healthy for 5 seconds.
 *
 * All sessionStorage access is try/catch-wrapped so private-mode
 * Chromium tabs or enterprise-policy-blocked storage fall through to
 * the manual reload UI instead of throwing inside the boundary
 * (Sol's finding: "storage failure can bypass recovery").
 */

const RELOAD_FLAG = 'lextrack_chunk_reload_attempted'
// Bumped from 30s → 5min after post-code panel finding (Grok): a slow CDN
// or tab-restore >30s clears the flag → next chunk error auto-reloads
// AGAIN → double-fetch storm during flaky-network deploys.  5min is well
// past any realistic bundle-fetch time even on 2G, but still short enough
// that a genuinely broken deploy doesn't stay stuck for hours.
const RELOAD_TTL_MS = 300_000 // 5 minutes

// Baked into main.[hash].js at build time by frontend/scripts/build-id.js
// (fires as the CRA `prebuild` hook, writes REACT_APP_BUILD_ID into
// .env.local, CRA picks it up automatically via webpack DefinePlugin).
// Falls back to 'unknown' if the prebuild script didn't run (dev mode
// on Windows without the prestart hook, etc.); 'unknown' behaves like
// a permanent build id — flags with different prefixes never match,
// which is safe but disables cross-deploy suppression.
const CURRENT_BUILD_ID = process.env.REACT_APP_BUILD_ID || 'unknown'

function isChunkLoadError (err) {
  if (!err) return false
  if (err.name === 'ChunkLoadError') return true
  const msg = err.message || String(err)
  return /Loading (?:CSS )?chunk .* failed/i.test(msg) ||
         /Failed to fetch dynamically imported module/i.test(msg) ||
         /error loading dynamically imported module/i.test(msg)
}

/**
 * Read the reload flag with build-id scoping.
 *
 * Flag format (post T2-B.next.A): `BUILD_ID:TIMESTAMP`
 * Legacy format (pre T2-B.next.A): bare `TIMESTAMP` string
 *
 * Returns:
 *   null     — flag absent, malformed, expired, from a DIFFERENT build
 *              (all mean "no active reload attempt from this build";
 *              boundary will auto-reload once if a chunk error hits)
 *   number   — timestamp of an active reload attempt from THIS build
 *              (boundary will show manual UI, no auto-reload loop)
 *
 * Panel-review-driven rules (Sol + Kimi + Grok + Opus 4.8):
 *   - Legacy bare-number values MUST be treated as null (no build_id
 *     to compare, so this build cannot claim ownership of the flag) —
 *     otherwise a client upgrading mid-crash-loop gets stuck.
 *   - Different-build-id values MUST be treated as null (allow the
 *     new build one auto-reload attempt against the stale flag).
 *   - Malformed / storage-error values MUST be treated as null (safest
 *     default; storage failures fall through to boundary manual UI).
 */
function readReloadFlag () {
  let raw
  try {
    raw = sessionStorage.getItem(RELOAD_FLAG)
  } catch (e) {
    return null
  }
  if (!raw || typeof raw !== 'string') return null

  // Modern format: BUILD_ID:TIMESTAMP
  const colonIdx = raw.lastIndexOf(':')
  if (colonIdx > 0) {
    const flagBuildId = raw.slice(0, colonIdx)
    const tsStr = raw.slice(colonIdx + 1)
    const ts = parseInt(tsStr, 10)
    if (isNaN(ts)) return null
    // Different build → new deploy shipped after the flag was set → allow
    // one auto-reload against the new build
    if (flagBuildId !== CURRENT_BUILD_ID) {
      // Also proactively clear the stale flag so subsequent runs see a
      // clean slate without depending on TTL
      try { sessionStorage.removeItem(RELOAD_FLAG) } catch (e) {}
      return null
    }
    // Same build — check TTL
    if (Date.now() - ts > RELOAD_TTL_MS) {
      try { sessionStorage.removeItem(RELOAD_FLAG) } catch (e) {}
      return null
    }
    return ts
  }

  // Legacy bare-timestamp format from pre-T2-B.next.A clients — no build
  // scope, safest to treat as null (allow one reload) rather than as an
  // active attempt from an unknown build.  Also clear it so we don't keep
  // hitting the legacy path.
  try { sessionStorage.removeItem(RELOAD_FLAG) } catch (e) {}
  return null
}

function writeReloadFlag () {
  try {
    sessionStorage.setItem(RELOAD_FLAG, `${CURRENT_BUILD_ID}:${Date.now()}`)
  } catch (e) {}
}

class ChunkLoadErrorBoundary extends React.Component {
  constructor (props) {
    super(props)
    this.state = { error: null }
    this.handleReload = this.handleReload.bind(this)
  }

  static getDerivedStateFromError (error) {
    // Always store — render() decides whether to show fallback UI
    // (chunk errors) or re-throw (non-chunk errors → SentryErrorBoundary)
    return { error }
  }

  componentDidCatch (error, info) {
    // Side effect only — no re-throw.  render() handles propagation.
    if (!isChunkLoadError(error)) return
    // Auto-reload ONCE (deploy-mid-session case).  If flag is already
    // set, we already tried and this is a second failure — show the
    // manual reload UI instead.  Use try/catch on the reload itself
    // because some Electron sandboxes throw on programmatic reload.
    try {
      if (!readReloadFlag()) {
        writeReloadFlag()
        // T2-B.next.B — belt-and-suspenders draft flush.  Primary
        // durability is debounced write-through in useDraft (writes
        // ~300ms after every state change), so most in-flight drafts
        // are already persisted.  This catches the corner case where
        // the user typed a keystroke <300ms before the chunk error hit.
        // flushAllDrafts NEVER throws — one bad snapshot cannot block
        // the reload (per panel finding — bad flush blocking reload
        // would strand user on broken screen).
        try { flushAllDrafts() } catch (e) {}
        window.location.reload()
      }
    } catch (e) {
      // Fall through to manual UI
    }
  }

  handleReload () {
    // Deliberately do NOT clear the flag here.  If this manual reload
    // also fails, the flag being set means we show the manual UI again
    // instead of auto-reload-looping.  The flag only clears via
    // useResetChunkReloadFlag() after 5s of demonstrably healthy mount.
    window.location.reload()
  }

  render () {
    const err = this.state.error
    if (err) {
      if (!isChunkLoadError(err)) {
        // Non-chunk errors propagate cleanly via render-throw.  React 18
        // catches this at the parent boundary (SentryErrorBoundary) with
        // full stack trace preserved.
        throw err
      }
      return (
        <div className="flex items-center justify-center h-screen bg-gray-50">
          <div className="max-w-md mx-4 bg-white rounded-lg shadow-lg p-8 text-center">
            <div className="text-5xl mb-4">🔄</div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              Update available
            </h2>
            <p className="text-sm text-gray-600 mb-6">
              A new version of Omnia Practice Management was deployed while
              this window was open.  Reload to pick up the latest changes —
              your work is safe (draft state and timers persist).
            </p>
            <button
              onClick={this.handleReload}
              className="bg-primary-600 hover:bg-primary-700 text-white font-semibold rounded-lg px-6 py-2.5 transition-colors"
            >
              Reload Omnia
            </button>
            <p className="text-xs text-gray-400 mt-4">
              If this happens repeatedly, your network may be blocking assets
              from <code className="bg-gray-100 px-1 rounded">iuris.mcmnxcloud.com</code>.
            </p>
          </div>
        </div>
      )
    }
    return this.props.children
  }
}

/**
 * useResetChunkReloadFlag — clear the reload guard after a successful
 * mount that lasted >5s.  MUST be called from inside the boundary's
 * children (not at the Layout top-level), so it only fires when the
 * boundary is NOT in error state.  Prevents the "flag clears while
 * broken → next chunk error auto-reloads → same broken state" loop
 * that plain top-level placement would create (post-code review by
 * Kimi + Grok).
 *
 * Recommended placement: inside a small `InnerRoute` component that
 * lives INSIDE `<Suspense>` alongside `<Outlet />` — that way the hook
 * mounts only when the lazy Outlet content successfully rendered, and
 * unmounts on suspend/error.
 */
export function useResetChunkReloadFlag () {
  React.useEffect(() => {
    const t = setTimeout(() => {
      try { sessionStorage.removeItem(RELOAD_FLAG) } catch (e) {}
    }, 5000)
    return () => clearTimeout(t)
  }, [])
}

/**
 * ResetChunkReloadFlag — component wrapper for useResetChunkReloadFlag.
 * Drop as a sibling of <Outlet /> INSIDE the boundary's Suspense so it
 * mounts only when the lazy Outlet content successfully rendered.  If
 * boundary is in error state OR Suspense is suspended, this component
 * never mounts and the flag stays set — protecting against the
 * "manual reload also fails → flag cleared → auto-reload loop" pattern
 * the top-level placement would allow.
 */
export function ResetChunkReloadFlag () {
  useResetChunkReloadFlag()
  return null
}

export default ChunkLoadErrorBoundary
